import React from 'react';
import EmptyState from './EmptyState';

/**
 * Tabela que vira cartão no celular.
 *
 * No desktop é uma tabela normal. Abaixo de 640 px cada linha vira um cartão, e
 * é aí que o `papel` de cada coluna importa: sem ele, toda célula virava uma
 * linha "RÓTULO ....... valor" com o mesmo peso visual, então um lançamento de
 * nove colunas ficava um bloco de nove linhas iguais — o produtor rolava muito e
 * não achava o que importa.
 *
 * Papéis (`papel` na coluna), todos opcionais:
 *
 * - `titulo`     identifica o registro. Forte, sem rótulo, primeira linha.
 * - `destaque`   o número que importa (quase sempre dinheiro). Grande, à direita,
 *                na mesma linha do título.
 * - `subtitulo`  contexto (data, categoria). Pequeno e discreto, sem rótulo.
 * - `chip`       badge já autoexplicativo (ENTRADA/SAÍDA). Sem rótulo.
 * - `detalhe`    padrão: rótulo + valor, o comportamento antigo, para o que
 *                sobra.
 * - `oculto`     não aparece no celular. Para coluna redundante com o título ou
 *                que só faz sentido na visão ampla.
 *
 * Sem nenhum papel declarado o cartão se comporta como antes, então as tabelas
 * que não foram revisadas continuam funcionando.
 *
 * Colunas com `ocultarSeVazia` desaparecem quando nenhuma linha tem valor —
 * útil porque muitos campos de produção são opcionais.
 *
 * @param colunas [{ chave, titulo, render?, alinhar?, ocultarSeVazia?, papel? }]
 * @param linhas  array de objetos
 * @param chaveDe função que devolve a key de cada linha
 * @param rodape  array de células já renderizadas, alinhado com as colunas
 * @param vazio   props do EmptyState quando não há linhas
 */
export default function DataTable({ colunas, linhas, chaveDe, rodape, vazio, densa }) {
  const visiveis = colunas.filter(c => {
    if (!c.ocultarSeVazia) return true;
    return linhas.some(l => {
      const v = c.valor ? c.valor(l) : l[c.chave];
      return v != null && v !== '' && v !== 0;
    });
  });

  if (!linhas || linhas.length === 0) {
    return <EmptyState compacto {...(vazio || { titulo: 'Nada por aqui ainda' })} />;
  }

  return (
    <div className="tabela-wrap">
      <table className={`tabela ${densa ? 'tabela--densa' : ''}`}>
        <thead>
          <tr>
            {visiveis.map(c => (
              <th key={c.chave} className={c.alinhar === 'direita' ? 'ta-direita' : undefined}>
                {c.titulo}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {linhas.map((l, i) => (
            <tr key={chaveDe ? chaveDe(l) : i}>
              {visiveis.map(c => (
                <td
                  key={c.chave}
                  // O rótulo alimenta o `content: attr(data-label)` do modo cartão.
                  // Papéis sem rótulo (título, destaque, subtítulo, chip) não
                  // recebem para o CSS não ter que desligar caso a caso.
                  data-label={semRotulo(c.papel) ? undefined : c.titulo}
                  data-papel={c.papel || undefined}
                  className={c.alinhar === 'direita' ? 'ta-direita' : undefined}
                >
                  {c.render ? c.render(l) : (l[c.chave] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {rodape && (
          <tfoot>
            <tr>
              {visiveis.map((c, i) => (
                <td
                  key={c.chave}
                  data-label={c.titulo}
                  // O rodapé não usa papéis: é sempre rótulo + total, porque
                  // total sem rótulo no celular vira uma pilha de números soltos.
                  className={c.alinhar === 'direita' ? 'ta-direita' : undefined}
                >
                  {rodape[i] ?? ''}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

function semRotulo(papel) {
  return papel === 'titulo' || papel === 'destaque' || papel === 'subtitulo' || papel === 'chip';
}
